import type { AxiosInstance } from 'axios';
import { unwrap, unwrapItems, type ObjectResponse } from '../core/envelope';

/**
 * `CustomerAddress` — JWT-customerId-scoped shipping address book.
 * Mirrors the schema in BACKEND_AGENT_PROMPTS.md Item 3. Until the
 * live endpoints ship, every call throws via the standard envelope
 * unwrap → the `decorateError` interceptor surfaces the actual server
 * message; the calling page falls back to localStorage on error.
 */
export interface CustomerAddress {
  customerAddressId?: string;
  customerId?: string; // server-resolved from JWT, never set client-side
  label?: string;
  fullName?: string;
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  pincode?: string;
  /** ITU-T country calling code, e.g. "91". */
  country?: string;
  phone?: string;
  isDefault?: boolean;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export type CustomerAddressCreate = Omit<
  CustomerAddress,
  'customerAddressId' | 'customerId' | 'createdAt' | 'updatedAt'
>;

export interface CustomerAddressService {
  /** `POST /api/auth/me/addresses` — list this customer's addresses. */
  mine(): Promise<CustomerAddress[]>;
  /** `POST /api/auth/me/addresses/Create { items }` — bulk create. */
  create(items: CustomerAddressCreate[]): Promise<CustomerAddress[]>;
  /** `PUT /api/auth/me/addresses { items }` — bulk update. */
  update(items: CustomerAddress[]): Promise<CustomerAddress[]>;
  /** `POST /api/auth/me/addresses/Delete { CustomerAddressId }` — soft-delete. */
  remove(id: string): Promise<void>;
}

export function createCustomerAddressService(
  client: AxiosInstance,
): CustomerAddressService {
  return {
    mine: () =>
      client
        .post<ObjectResponse<CustomerAddress[] | { items?: CustomerAddress[] }>>(
          '/auth/me/addresses',
          {},
        )
        .then((r) => unwrapItems<CustomerAddress>(r.data)),

    create: (items) =>
      client
        .post<ObjectResponse<CustomerAddress[] | { items?: CustomerAddress[] }>>(
          '/auth/me/addresses/Create',
          { items },
        )
        .then((r) => unwrapItems<CustomerAddress>(r.data)),

    update: (items) =>
      client
        .put<ObjectResponse<CustomerAddress[] | { items?: CustomerAddress[] }>>(
          '/auth/me/addresses',
          { items },
        )
        .then((r) => unwrapItems<CustomerAddress>(r.data)),

    remove: (id) =>
      client
        .post<ObjectResponse<null>>('/auth/me/addresses/Delete', {
          CustomerAddressId: id,
        })
        .then((r) => {
          unwrap(r.data, { allowNullData: true });
        }),
  };
}
